/**
 * Remembering where a visitor was headed when their session turned out to be missing.
 *
 * Links that arrive from outside the app — the "Reconnect your account" email above all —
 * point at a specific page. Anyone following one without a live session was bounced to
 * the login screen and then dropped on the dashboard, because login always navigated to
 * `/app`. The email told them to open Accounts and press Reconnect, and the link did not
 * take them there. That hits exactly the people this mail is written for: someone whose
 * token died is usually someone who has not visited in a while, so they are the least
 * likely to still be signed in.
 */

const STORAGE_KEY = "connexxtion:returnTo";

/**
 * Only same-origin paths inside the authenticated area are worth returning to, and only
 * those may be trusted: an attacker who can put a value here would otherwise turn our
 * login into an open redirect. A leading `//` or any scheme is a URL to somewhere else,
 * whatever it looks like.
 *
 * @param {string} path - Candidate path, as `pathname + search`.
 * @returns {boolean} True when it is safe to send a freshly logged-in user there.
 */
export function isSafeReturnPath(path) {
  if (typeof path !== "string" || path.length < 2) return false;
  if (!path.startsWith("/app/")) return false;
  if (path.startsWith("//")) return false;
  if (path.includes("\\")) return false;
  // A scheme (`javascript:`, `https:`) can only appear here if someone put it there.
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return false;
  // Whitespace or a control byte can only be here if someone smuggled it in.
  if (/\s/.test(path)) return false;
  if ([...path].some((ch) => ch.charCodeAt(0) < 0x20 || ch.charCodeAt(0) === 0x7f)) {
    return false;
  }

  return true;
}

/**
 * Store where the visitor was going, if it is somewhere we would send them back to.
 *
 * @param {string} path - `pathname + search` at the moment the session was rejected.
 * @returns {void}
 */
export function rememberReturnTo(path) {
  if (!isSafeReturnPath(path)) return;

  try {
    globalThis.sessionStorage?.setItem(STORAGE_KEY, path);
  } catch (e) {
    // Private mode or a storage quota — losing the destination is not worth an error.
  }
}

/**
 * Read the stored destination and forget it, so a later ordinary login does not bounce
 * the user somewhere they no longer expect.
 *
 * @returns {string|null} The path to continue to, or null when there is nothing pending.
 */
export function takeReturnTo() {
  let path = null;

  try {
    path = globalThis.sessionStorage?.getItem(STORAGE_KEY) ?? null;
    globalThis.sessionStorage?.removeItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }

  return isSafeReturnPath(path) ? path : null;
}

/**
 * Drop any pending destination — used when the user signs out deliberately.
 *
 * @returns {void}
 */
export function clearReturnTo() {
  try {
    globalThis.sessionStorage?.removeItem(STORAGE_KEY);
  } catch (e) {
    // Nothing to do.
  }
}
